import React from 'react';
import styled from 'styled-components';
import { FormField } from '../services/formConfigService';
import { RepeatingSection as RepeatingSectionType } from '../utils/repeatingSectionUtils';

interface RepeatingSectionProps {
  section: RepeatingSectionType;
  rows: any[];
  formData: any;
  onAddRow: () => void;
  onRemoveRow: (rowIndex: number) => void;
  onFieldChange: (rowIndex: number, fieldName: string, value: any) => void;
}

const RepeatingSectionContainer = styled.div`
  margin: 20px 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`;

const AddButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px; /* Minimum width to prevent cramping */
  border-collapse: collapse;
  background: white;
  border-radius: 4px;
  overflow: hidden;
  
  th {
    background: #f5f5f5;
    padding: 12px;
    text-align: left;
    font-weight: 600;
    color: #555;
    border-bottom: 2px solid #e0e0e0;
  }
  
  td {
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover {
    background: #f9f9f9;
  }
`;

const DeleteButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #da190b;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
  
  span {
    font-weight: 600;
    color: #555;
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-style: italic;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 60px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const RepeatingSection: React.FC<RepeatingSectionProps> = ({
  section,
  rows,
  formData,
  onAddRow,
  onRemoveRow,
  onFieldChange
}) => {
  // HOOKS MUST BE AT THE TOP - before any conditional returns
  // Detect mobile screen and force card layout
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Filter to only show visible fields
  const visibleFields = section.fields.filter(field => field.isVisible !== false);
  
  // Force card layout on mobile, regardless of database setting
  const effectiveLayout = isMobile ? 'card' : (section.layout || 'card');
  
  // If no visible fields, don't render the section at all
  if (visibleFields.length === 0) {
    return null;
  }
  
  const canAddRow = rows.length < section.maxRows;
  const canRemoveRow = rows.length > section.minRows;

  const handleAddRow = () => {
    if (canAddRow) {
      onAddRow();
    }
  };

  const handleRemoveRow = (rowIndex: number) => {
    if (canRemoveRow) {
      if (window.confirm('Are you sure you want to delete this row?')) {
        onRemoveRow(rowIndex);
      }
    }
  };

  const getRowTitle = (rowIndex: number): string => {
    return section.rowTitleTemplate.replace('{index}', String(rowIndex + 1));
  };

  // Simple field renderer for repeating section rows
  const renderFieldInRow = (field: FormField, value: any, onChange: (value: any) => void) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <Input
            type={field.fieldType}
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder || field.label}
          />
        );

      case 'textarea':
        return (
          <TextArea
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder || field.label}
          />
        );
      
      case 'dropdown':
        return (
          <Select value={value || ''} onChange={handleChange}>
            <option value="">Select {field.label}</option>
            {field.staticOptions && Array.isArray(field.staticOptions) && field.staticOptions.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.text || opt.label || opt.value}
              </option>
            ))}
          </Select>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={handleChange}
          />
        );
      
      case 'datetime-local':
        return (
          <Input
            type="datetime-local"
            value={value || ''}
            onChange={handleChange}
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder={field.placeholder || field.label}
          />
        );
    }
  };

  if (rows.length === 0 && section.triggerType === 'quantity') {
    return (
      <RepeatingSectionContainer>
        <SectionHeader>
          <SectionTitle>{section.sectionName}</SectionTitle>
        </SectionHeader>
        <EmptyState>
          Enter a quantity in the trigger field to create rows
        </EmptyState>
      </RepeatingSectionContainer>
    );
  }

  return (
    <RepeatingSectionContainer>
      <SectionHeader>
        <SectionTitle>{section.sectionName}</SectionTitle>
        {section.allowAddRemove && section.triggerType !== 'quantity' && (
          <AddButton onClick={handleAddRow} disabled={!canAddRow}>
            <span>+</span>
            <span>Add Row</span>
          </AddButton>
        )}
      </SectionHeader>

      {rows.length === 0 ? (
        <EmptyState>No rows added yet. Click "Add Row" to get started.</EmptyState>
      ) : effectiveLayout === 'card' ? (
        // Card layout (mobile-friendly, or forced on mobile)
        <CardContainer>
          {rows.map((row, rowIndex) => (
            <Card key={rowIndex}>
              <CardHeader>
                <span>{getRowTitle(rowIndex)}</span>
                {section.showDeleteButton && (
                  <DeleteButton
                    onClick={() => handleRemoveRow(rowIndex)}
                    disabled={!canRemoveRow}
                  >
                    Delete
                  </DeleteButton>
                )}
              </CardHeader>
              <CardBody>
                {visibleFields.map(field => (
                  <FormGroup key={field.fieldName}>
                    <Label>
                      {field.label}
                      {field.isMandatory && <span style={{ color: 'red' }}> *</span>}
                    </Label>
                    {renderFieldInRow(
                      field,
                      row[field.fieldName],
                      (value: any) => onFieldChange(rowIndex, field.fieldName, value)
                    )}
                  </FormGroup>
                ))}
              </CardBody>
            </Card>
          ))}
        </CardContainer>
      ) : effectiveLayout === 'table' ? (
        // Table layout (desktop only, never on mobile)
        <TableWrapper>
          <Table>
          <thead>
            <tr>
              {section.showRowNumbers && <th style={{ width: '50px' }}>#</th>}
              {visibleFields.map(field => (
                <th key={field.fieldName}>
                  {field.label}
                  {field.isMandatory && <span style={{ color: 'red' }}> *</span>}
                </th>
              ))}
              {section.showDeleteButton && <th style={{ width: '100px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {section.showRowNumbers && (
                  <td style={{ textAlign: 'center', fontWeight: '600', color: '#666' }}>
                    {rowIndex + 1}
                  </td>
                )}
                {visibleFields.map(field => (
                  <td key={field.fieldName}>
                    {renderFieldInRow(
                      field,
                      row[field.fieldName],
                      (value: any) => onFieldChange(rowIndex, field.fieldName, value)
                    )}
                  </td>
                ))}
                {section.showDeleteButton && (
                  <td>
                    <DeleteButton
                      onClick={() => handleRemoveRow(rowIndex)}
                      disabled={!canRemoveRow}
                    >
                      Delete
                    </DeleteButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        </TableWrapper>
      ) : null}
    </RepeatingSectionContainer>
  );
};

export default RepeatingSection;
